import { ResponsiveLine } from "@nivo/line";
import { useEffect, useMemo, useState } from "react";

import { Text } from "@design-system/Text/Text";

const useCssVar = (varName: string, fallback: string) => {
  const [value, setValue] = useState(fallback);
  useEffect(() => {
    const root = document.documentElement;
    const cssValue = getComputedStyle(root).getPropertyValue(varName).trim();
    if (cssValue) setValue(cssValue);
  }, [varName]);
  return value;
};

type Data = {
  id: string;
  color: string;
  data: {
    x: string;
    y: number;
  }[];
}[];

const defaultData: Data = [];

type Props = {
  data: Data;
  xAxisName: string;
  yAxisName: string;
  weekendDates?: string[];
  tooltipLabel?: string;
};

export const LineChart = ({
  data = defaultData,
  xAxisName,
  yAxisName,
  weekendDates = [],
  tooltipLabel = "Value",
}: Props) => {
  const axisColor = useCssVar("--text-dark", "#CCC");
  const ticksColor = useCssVar("--border-default-primary", "#CCC");
  const labelColor = useCssVar("--text-light", "#CCC");
  const tooltipColor = useCssVar("--text-light", "#CCC");
  const tooltipBackgroundColor = useCssVar("--text-dark", "#CCC");
  const weekendTickColor = useCssVar("--primary", "#FF6B6B");
  const lineColor = useCssVar("--primary", "#FF6B6B");

  const yValues = data.flatMap((series) => series.data.map((d) => d.y));
  const minY = Math.min(...yValues, 0);
  const maxY = Math.max(...yValues, 0);

  // Generate at most 5 tick values with good spacing
  const generateTickValues = (
    min: number,
    max: number,
    maxTicks: number = 5,
  ) => {
    if (min === max) return [min];

    const range = max - min;
    const roughStep = range / (maxTicks - 1);

    // Find a nice step size (round to nice numbers like 1, 2, 5, 10, 20, 50, etc.)
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalizedStep = roughStep / magnitude;

    let niceStep;
    if (normalizedStep <= 1) niceStep = 1;
    else if (normalizedStep <= 2) niceStep = 2;
    else if (normalizedStep <= 5) niceStep = 5;
    else niceStep = 10;

    const step = niceStep * magnitude;

    // Generate tick values
    const tickValues = [];
    const start = Math.floor(min / step) * step;

    for (let i = start; i <= max + step * 0.1; i += step) {
      if (i >= min - step * 0.1) {
        tickValues.push(Math.round(i * 100) / 100); // Round to avoid floating point issues
      }
    }

    return tickValues.slice(0, maxTicks);
  };

  const tickValues = generateTickValues(minY, maxY);

  // Apply the primary color to the line
  const dataWithLineColor = data.map((series) => ({
    ...series,
    color: lineColor,
  }));

  // Show up to 30 x-axis labels, evenly spaced if more than 30, but always 30 if possible
  const xTickValues = useMemo(() => {
    if (!data.length) return [];
    const allX = data[0].data.map((d) => d.x);
    if (allX.length > 30) {
      // Always show exactly 30 ticks, including first and last
      const step = (allX.length - 1) / 29;
      return Array.from(
        { length: 30 },
        (_, i) => allX[Math.round(i * step)],
      ).filter(Boolean);
    }
    return allX;
  }, [data]);

  return (
    <ResponsiveLine
      data={dataWithLineColor}
      colors={() => lineColor}
      margin={{ top: 10, right: 20, bottom: 70, left: 70 }}
      xScale={{ type: "point" }}
      yScale={{
        type: "linear",
        min: 0,
        max: "auto",
        stacked: false,
        reverse: false,
      }}
      layers={[
        "grid",
        "markers",
        "axes",
        "areas",
        "crosshair",
        "lines",
        "points",
        "slices",
        "mesh",
        "legends",
        ({ xScale, innerHeight }) => {
          if (!weekendDates.length) return null;

          return (
            <g>
              {weekendDates.map((date, index) => {
                const x = xScale(date);
                if (x === undefined) return null;

                return (
                  <rect
                    key={`weekend-${index}`}
                    x={x - 15}
                    y={0}
                    width={30}
                    height={innerHeight}
                    fill={weekendTickColor}
                    fillOpacity={0.1}
                    pointerEvents="none"
                  />
                );
              })}
            </g>
          );
        },
      ]}
      curve="monotoneX"
      yFormat=">-.2f"
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: -45,
        legend: xAxisName,
        legendOffset: 36,
        legendPosition: "middle",
        truncateTickAt: 0,
        tickValues: xTickValues,
        renderTick: (tick) => (
          <g transform={`translate(${tick.x},${tick.y})`}>
            <line x1={0} x2={0} y1={0} y2={5} stroke={ticksColor} />
            <text
              x={0}
              y={0}
              textAnchor="end"
              dominantBaseline="text-before-edge"
              transform="rotate(-45)"
              style={{
                fontSize: "10px",
                fill: weekendDates.includes(String(tick.value))
                  ? weekendTickColor
                  : axisColor,
                fontWeight: weekendDates.includes(String(tick.value))
                  ? "bold"
                  : "normal",
              }}
            >
              {tick.value}
            </text>
          </g>
        ),
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: yAxisName,
        legendOffset: -40,
        legendPosition: "middle",
        truncateTickAt: 10,
        format: (value: number) => Math.round(value),
        tickValues,
      }}
      tooltip={({ point }) => (
        <div
          style={{
            color: tooltipColor,
            background: tooltipBackgroundColor,
            fontSize: "12px",
            padding: "6px 12px",
            borderRadius: "4px",
          }}
        >
          <Text color="textLight">
            {point.data.x} <br />
            {tooltipLabel}:&nbsp;{point.data.y}
          </Text>
        </div>
      )}
      pointSize={10}
      pointColor={{ theme: "background" }}
      pointBorderWidth={2}
      pointBorderColor={{ from: "serieColor" }}
      pointLabel="data.yFormatted"
      pointLabelYOffset={-12}
      enableTouchCrosshair={true}
      useMesh={true}
      animate={true}
      theme={{
        axis: {
          ticks: { text: { fill: axisColor } },
          legend: { text: { fill: axisColor } },
        },
        grid: {
          line: {
            stroke: ticksColor,
          },
        },
        labels: {
          text: { fill: labelColor },
        },
        tooltip: {
          container: {
            color: tooltipColor,
            background: tooltipBackgroundColor,
            fontSize: "12px",
          },
        },
      }}
    />
  );
};
